import { useEffect, useState } from 'react';
import { budgetAPI, categoryAPI } from '../api';
import { Plus, Trash2, AlertCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { format, addMonths } from 'date-fns';

export default function Budgets() {
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [budgetsRes, categoriesRes] = await Promise.all([
        budgetAPI.getStatus(),
        categoryAPI.getAll('expense'),
      ]);
      setBudgets(budgetsRes.data);
      setCategories(categoriesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading budgets:', error);
      toast.error('Failed to load budgets');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this budget?')) return;
    try {
      await budgetAPI.delete(id);
      toast.success('Budget deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting budget:', error);
      toast.error('Failed to delete budget');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading budgets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 p-6 md:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-heading text-green-900 mb-2">
              Budgets
            </h1>
            <p className="text-stone-500 text-lg">Set spending limits and track progress</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="add-budget-btn"
            className="bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95"
          >
            <Plus className="inline-block w-5 h-5 mr-2" />
            Add Budget
          </button>
        </div>

        {budgets.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
            <TrendingUp className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500 mb-4">No budgets set yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all"
            >
              Create Your First Budget
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="budgets-list">
            {budgets.map((budget) => {
              const statusColor =
                budget.status === 'over_budget'
                  ? 'bg-red-100 border-red-300 text-red-900'
                  : budget.status === 'warning'
                  ? 'bg-yellow-100 border-yellow-300 text-yellow-900'
                  : 'bg-green-100 border-green-300 text-green-900';

              const progressColor =
                budget.status === 'over_budget'
                  ? 'bg-red-700'
                  : budget.status === 'warning'
                  ? 'bg-yellow-600'
                  : 'bg-green-900';

              return (
                <div
                  key={budget.id}
                  className="bg-white rounded-2xl p-6 border border-stone-200 hover:shadow-md transition-all"
                  data-testid={`budget-${budget.id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold font-heading text-green-900 mb-1">
                        {budget.category}
                      </h3>
                      <p className="text-sm text-stone-500">
                        {format(new Date(budget.period_start_date), 'MMM d')} -{' '}
                        {format(new Date(budget.period_end_date), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(budget.id)}
                      data-testid={`delete-budget-${budget.id}`}
                      className="p-2 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="text-2xl font-bold text-stone-900 tabular-nums">
                        {budget.spent.toFixed(2)}
                      </span>
                      <span className="text-sm text-stone-500">
                        of {budget.budget_amount.toFixed(2)} SAR
                      </span>
                    </div>
                    <div className="w-full bg-stone-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`${progressColor} h-3 rounded-full transition-all`}
                        style={{
                          width: `${Math.min(budget.percentage_used, 100)}%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold border ${statusColor}`}
                      data-testid={`budget-status-${budget.id}`}
                    >
                      {budget.percentage_used.toFixed(0)}% used
                    </span>
                    <span className="text-sm font-medium text-stone-600 tabular-nums">
                      {budget.remaining >= 0 ? 'Remaining:' : 'Over by:'}{' '}
                      {Math.abs(budget.remaining).toFixed(2)} SAR
                    </span>
                  </div>

                  {budget.status === 'over_budget' && (
                    <div className="mt-4 flex items-start bg-red-50 border border-red-200 rounded-xl p-3">
                      <AlertCircle className="w-5 h-5 text-red-700 mr-2 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-800">
                        You've exceeded your budget for this category
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddBudgetModal
          categories={categories}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function AddBudgetModal({ categories, onClose, onSuccess }) {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [formData, setFormData] = useState({
    period_type: 'monthly',
    period_start_date: monthStart.toISOString().split('T')[0],
    period_end_date: monthEnd.toISOString().split('T')[0],
    category: '',
    budget_amount: '',
    warnings_threshold_percentage: 80,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await budgetAPI.create({
        ...formData,
        budget_amount: parseFloat(formData.budget_amount),
      });
      toast.success('Budget created successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error creating budget:', error);
      toast.error('Failed to create budget');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-budget-modal"
      >
        <h2 className="text-2xl font-bold font-heading text-green-900 mb-6">Add Budget</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              required
              data-testid="input-category"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Budget Amount (SAR)</label>
            <input
              type="number"
              step="0.01"
              value={formData.budget_amount}
              onChange={(e) => setFormData({ ...formData, budget_amount: e.target.value })}
              required
              data-testid="input-budget-amount"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">Period Type</label>
            <select
              value={formData.period_type}
              onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
              data-testid="input-period-type"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            >
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-2">Start Date</label>
              <input
                type="date"
                value={formData.period_start_date}
                onChange={(e) => setFormData({ ...formData, period_start_date: e.target.value })}
                required
                data-testid="input-start-date"
                className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-stone-700 mb-2">End Date</label>
              <input
                type="date"
                value={formData.period_end_date}
                onChange={(e) => setFormData({ ...formData, period_end_date: e.target.value })}
                required
                data-testid="input-end-date"
                className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-stone-700 mb-2">
              Warning Threshold (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.warnings_threshold_percentage}
              onChange={(e) =>
                setFormData({ ...formData, warnings_threshold_percentage: parseFloat(e.target.value) })
              }
              data-testid="input-warning-threshold"
              className="w-full bg-stone-50 border-stone-200 focus:border-green-900 focus:ring-2 focus:ring-green-900/20 rounded-xl h-12 px-4 transition-all"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              data-testid="modal-cancel-btn"
              className="flex-1 px-6 py-3 bg-stone-100 text-stone-900 rounded-full font-medium hover:bg-stone-200 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="modal-submit-btn"
              className="flex-1 px-6 py-3 bg-green-900 text-white rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
